import { PERMISSIONS } from "@/services/permission"
import { FEATURE_SETS } from "@/services/featureSet"
import { ENTITY_ICONS } from "@/services/entityIcons"
import { CATALOG_PACKETS, PACKET_TYPES, packetListRoute } from "@/services/catalogPackets"
import { isEntitled } from "@/services/entitlements"
import { hasPermission, hasPermissionsOfAll, hasPermissionsOfAtLeastOne } from "@/services/permissionService"
import { PROJECT_FORM_NAMES } from "@/store/project"
import { CUSTOMER_FORM_NAMES } from "@/store/customer"
import { MATERIAL_FORM_NAMES } from "@/store/material"
import { MACHINE_FORM_NAMES } from "@/store/machine"
import { ADDITIONAL_EFFORT_FORM_NAMES } from "@/store/additionalEffort"
import { PACKET_FORM_NAMES } from "@/store/packet"
import { EMPLOYEE_FORM_NAMES } from "@/components/shared/employee/LewEmployeeEdit/employeeForm"
import { VIEW_FILE_PERMISSIONS } from "@/services/fileBrowser"

// The application's destinations and create shortcuts, in one place. The navigation drawer renders
// them as its entries and the command palette (`LewCommandPalette`) offers the same list as
// "go to" / "create" commands, so a new section appears in both — and with the same permission
// gate — without being described twice.
//
// The `enabled` flags mirror what `router/navigation-guard.js` enforces for the target route:
// `requiresPermissions` is "at least one of", `requiresAllPermissions` is "all of", and
// `requiresFeatureSet` is the tenant's purchased add-on. Getting that wrong here would show an entry
// that bounces the user straight back to the dashboard (or, for a feature-set, to the add-on upsell).

// Employees and projects are readable with *any* of their field-level view permissions — the detail
// views hide the sections a role may not read, so a partial permission still means a usable page.
export const VIEW_USER_PERMISSIONS = [
  PERMISSIONS.VIEW_USER_GENERAL,
  PERMISSIONS.VIEW_USER_COMPANY_GENERAL,
  PERMISSIONS.VIEW_USER_COMPANY_PAY,
  PERMISSIONS.VIEW_USER_COMPANY_TOTAL_HOLIDAY_DAYS,
  PERMISSIONS.VIEW_USER_COMPANY_SOCIAL_INSURANCE,
  PERMISSIONS.VIEW_USER_COMPANY_SOCIAL_INSURANCE_NUMBER,
  PERMISSIONS.VIEW_USER_SYSTEM,
  PERMISSIONS.VIEW_USER_FILE,
  PERMISSIONS.VIEW_USER_EXTENDED,
]

export const VIEW_PROJECT_PERMISSIONS = [
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_GENERAL,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_CONSTRUCTIONREPORT,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_CONTACT,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_MACHINE,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_MATERIAL,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_ADDITIONAL_EFFORT,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_CATEGORY,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_USER,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_USERPOSITION,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_FILE,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_SETTINGS,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEINVOICE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_GENERAL,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_CONSTRUCTIONREPORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_CONTACT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_MACHINE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_MATERIAL,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_ADDITIONAL_EFFORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_CATEGORY,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_USER,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_USERPOSITION,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_FILE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_SETTINGS,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEINVOICE,
]

/**
 * Whether the current user may open each drawer destination, keyed by a stable gate id.
 *
 * Split out of the entry list so a gate can be asked for by name without building the whole list
 * (which needs an i18n composer for the titles). The onboarding walkthrough's catalog does exactly
 * that — see {@link isNavEntryEnabled}.
 *
 * The values are thunks, not computeds: `hasPermissionsOfAtLeastOne` builds a fresh computed per
 * call, and these are evaluated on every render anyway.
 */
const NAV_GATES = {
  "nav-employee": () => hasPermissionsOfAtLeastOne(VIEW_USER_PERMISSIONS).value,
  "nav-category": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_CATEGORY_GENERAL]).value,
  "nav-material": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_MATERIAL_GENERAL]).value,
  "nav-machine": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_MACHINE_GENERAL]).value,
  "nav-additionaleffort": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_ADDITIONAL_EFFORT_GENERAL]).value,
  "nav-customer": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_CUSTOMER_GENERAL]).value,
  "nav-project": () => hasPermissionsOfAtLeastOne(VIEW_PROJECT_PERMISSIONS).value,
  "nav-userposition": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_USER_POSITION_GENERAL]).value,
  "nav-packet": () => hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_PACKET_GENERAL]).value,
  "nav-invoice": () =>
    hasPermissionsOfAtLeastOne([
      PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEINVOICE,
      PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEINVOICE,
    ]).value,
  "nav-costs": () =>
    hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_ASSIGNED_PROJECT_COSTS, PERMISSIONS.VIEW_FOREIGN_PROJECT_COSTS]).value,
  // Also gated on the add-on the tenant bought, not just the user's permission — mirroring the
  // route's `requiresFeatureSet`, exactly as the permission flags mirror its permission gate.
  // A tenant without the add-on has no personnel planning to navigate to at all.
  "nav-personnelplanning": () =>
    hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_PERSONNELPLANNING]).value &&
    isEntitled(FEATURE_SETS.PERSONNEL_PLANNING),
  // Zeiterfassung: the add-on plus either half of the feature. Recording your own day
  // (RECORD_OWN_TIME, which every role holds by default) or reviewing other people's
  // (VIEW_TIME_ENTRY_FOREIGN) — the entry leads to whichever page the user may open first, see
  // `timeTrackingEntryRoute`, and the feature's tab strip reaches the rest.
  "nav-timetracking": () =>
    hasPermissionsOfAtLeastOne([PERMISSIONS.RECORD_OWN_TIME, PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]).value &&
    isEntitled(FEATURE_SETS.TIME_TRACKING),
  // Reviewing and analysing *other people's* recorded time — the two palette-only destinations
  // below. Same requirement for both, so one gate: the add-on plus the permission that every
  // route touching somebody else's hours carries.
  "nav-timetracking-foreign": () =>
    hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]).value && isEntitled(FEATURE_SETS.TIME_TRACKING),
  // Tagesmeldungen, by either project scope; which ones somebody sees the backend decides per project. And
  // the feature-set, as on the routes: the entry's open count is polled through this gate too
  // (`store/daySubmission.ts`), so without it a workspace lacking the set would be asked for counts it
  // is refused.
  "nav-daysubmission": () =>
    hasPermissionsOfAtLeastOne([
      PERMISSIONS.VIEW_ASSIGNED_PROJECT_DAY_SUBMISSION,
      PERMISSIONS.VIEW_FOREIGN_PROJECT_DAY_SUBMISSION,
    ]).value && isEntitled(FEATURE_SETS.DAY_SUBMISSIONS),
  // Any of the eight file-bearing types is enough to have something to browse — the tree only ever
  // shows the branches the caller may read, so a partial permission still means a usable page. The
  // list lives in `services/fileBrowser.js` next to the type registry it belongs to.
  "nav-file": () => hasPermissionsOfAtLeastOne(VIEW_FILE_PERMISSIONS).value,
  "nav-settings": () => true,
}

/**
 * Whether the destination behind this gate id is available to the current user, without building the
 * entry list.
 *
 * A step of the onboarding walkthrough that describes a section names that section's gate and asks
 * here, so the permission lists stay in this file only — copying them into the walkthrough's catalog
 * would be a guaranteed drift bug the moment one of them changes.
 *
 * @param gateId one of the keys of [NAV_GATES], e.g. `"nav-personnelplanning"`
 * @returns false for an unknown id, so a typo hides a step rather than showing a broken one
 */
export function isNavEntryEnabled(gateId) {
  return NAV_GATES[gateId]?.() ?? false
}

/**
 * A catalogue's packets as a drawer entry of its own — only for somebody who may read packets but not
 * that catalogue. Everybody else finds the packets in the catalogue's own table; without this, a role
 * holding only `VIEW_PACKET_GENERAL` (the backend lets it stand alone) would have no way to its packets
 * but the command palette. The same page, which then shows the packets alone under their own name.
 * Sits where the catalogue's entry would be.
 */
function packetListFallback(type, catalogGate, i18n) {
  const entry = CATALOG_PACKETS[type]
  return {
    link: packetListRoute(type),
    icon: ENTITY_ICONS.packet,
    title: i18n.t(entry.packetsTitleKey),
    enabled: NAV_GATES["nav-packet"]() && !NAV_GATES[catalogGate](),
    activeFor: [entry.catalog],
  }
}

/**
 * The primary destinations — the drawer's main list, top to bottom.
 *
 * @param i18n the `useI18n()` composer of the calling component
 * @returns `{ link, icon, title, enabled }[]`, including the entries the user may not open
 *   (their `enabled` is false); call sites filter, so the ordering stays stable.
 */
export function navigationItems(i18n) {
  return [
    { link: { name: "Dashboard" }, icon: "mdi-view-dashboard", title: i18n.t("nav.dashboard"), enabled: true },
    {
      link: { name: "EmployeeOverview" },
      icon: ENTITY_ICONS.employee,
      title: i18n.t("nav.employee.overview"),
      enabled: NAV_GATES["nav-employee"](),
    },
    {
      link: { name: "MaterialOverview" },
      icon: ENTITY_ICONS.material,
      title: i18n.t("nav.material.overview"),
      enabled: NAV_GATES["nav-material"](),
      // Lit whatever the kind filter says: the drawer's `exact` compares the query too.
      activeFor: [CATALOG_PACKETS.MATERIAL.catalog],
    },
    packetListFallback("MATERIAL", "nav-material", i18n),
    {
      link: { name: "MachineOverview" },
      icon: ENTITY_ICONS.machine,
      title: i18n.t("nav.machine.overview"),
      enabled: NAV_GATES["nav-machine"](),
      activeFor: [CATALOG_PACKETS.MACHINE.catalog],
    },
    packetListFallback("MACHINE", "nav-machine", i18n),
    {
      link: { name: "AdditionalEffortOverview" },
      icon: ENTITY_ICONS.additionaleffort,
      title: i18n.t("nav.additionaleffort.overview"),
      enabled: NAV_GATES["nav-additionaleffort"](),
      activeFor: [CATALOG_PACKETS.ADDITIONAL_EFFORT.catalog],
    },
    packetListFallback("ADDITIONAL_EFFORT", "nav-additionaleffort", i18n),
    {
      link: { name: "UserPositionOverview" },
      icon: ENTITY_ICONS.userposition,
      title: i18n.t("nav.userposition.overview"),
      enabled: NAV_GATES["nav-userposition"](),
    },
    {
      link: { name: "CustomerOverview" },
      icon: ENTITY_ICONS.customer,
      title: i18n.t("nav.customer.overview"),
      enabled: NAV_GATES["nav-customer"](),
    },
    {
      link: { name: "ProjectOverview" },
      icon: ENTITY_ICONS.project,
      title: i18n.t("nav.project.overview"),
      enabled: NAV_GATES["nav-project"](),
    },
    {
      link: { name: "DaySubmissionOverview" },
      icon: ENTITY_ICONS.daysubmission,
      title: i18n.t("nav.daysubmission"),
      enabled: NAV_GATES["nav-daysubmission"](),
    },
    {
      link: { name: "HourlywageinvoiceWizard" },
      icon: "mdi-file-document-multiple",
      title: i18n.t("nav.invoice"),
      enabled: NAV_GATES["nav-invoice"](),
    },
    {
      link: { name: "FileBrowser" },
      icon: "mdi-folder-multiple-outline",
      title: i18n.t("nav.file.overview"),
      enabled: NAV_GATES["nav-file"](),
    },
    {
      link: { name: "ProjectCosts" },
      icon: "mdi-cash-clock",
      title: i18n.t("nav.project.costs"),
      enabled: NAV_GATES["nav-costs"](),
    },
    {
      link: { name: "PersonnelPlanning" },
      icon: "mdi-calendar-account",
      title: i18n.t("nav.personnelplanning"),
      enabled: NAV_GATES["nav-personnelplanning"](),
    },
    {
      link: timeTrackingEntryRoute(),
      icon: ENTITY_ICONS.timetracking,
      title: i18n.t("nav.timetracking"),
      enabled: NAV_GATES["nav-timetracking"](),
    },
  ]
}

/**
 * Where the drawer's Zeiterfassung entry leads: "Meine Zeiten" for anyone who records their own day,
 * the review queue for a reviewer whose role was kept out of recording. Linking such a reviewer to
 * "Meine Zeiten" would bounce them to the dashboard, and the review pages have no drawer entry of
 * their own — they are reached through the feature's tab strip.
 */
export function timeTrackingEntryRoute() {
  return hasPermission(PERMISSIONS.RECORD_OWN_TIME).value ? { name: "TimeTrackingMy" } : { name: "TimeTrackingReview" }
}

/** Destinations pinned to the bottom of the drawer, below the divider. */
export function navigationFooterItems(i18n) {
  return [
    {
      link: { name: "SettingsOverview" },
      icon: "mdi-cog",
      title: i18n.t("nav.settings"),
      enabled: NAV_GATES["nav-settings"](),
      // The PDF-Vorlagen' editors are pages of their own, opened from a settings tab.
      activeFor: ["PdfLetterheadEditor", "PdfTemplateEditor"],
    },
  ]
}

/**
 * Pages that are deliberately **not** in the drawer, but should still be findable by name.
 *
 * Each has exactly one narrow way in — the notification overview only from the bell's "show all",
 * the activity log only from a "show all" on an activity list, a catalogue's packets only from
 * the kind filter above that catalogue's table (the list of all packets has none at all). That is the right entry point when
 * you are already looking at the thing, and no entry point at all when you are not. Being findable
 * by name in the palette fixes that without adding the second sidebar entry both were kept out of.
 *
 * The notification, changelog and activity routes carry no permission gate (all three scope their contents
 * server-side, so an empty list is the correct "no access" result). The Zeiterfassung entries do:
 * they are about *other people's* recorded hours, so offering them to somebody who would be bounced
 * would be the same broken promise the drawer's `enabled` flags exist to prevent.
 */
export function searchOnlyDestinations(i18n) {
  // The name the drawer gives the feature these five pages live in, exactly as `settingsTabItems`
  // names the view its tabs live in. A palette row shows title and parent and nothing else, so
  // without it "Mitarbeiter" here and "Mitarbeiter" in the drawer are two rows that differ by icon
  // alone. It is matched on as well as shown (`scoreOf(item.title, item.parent)`), which is the
  // second half of the point: typing the feature's name finds its pages.
  //
  // The notification, changelog and activity entries below belong to no such feature and carry none.
  const parent = i18n.t("nav.timetracking")
  return [
    {
      link: { name: "NotificationOverview" },
      icon: "mdi-bell-outline",
      title: i18n.t("notification.title"),
      enabled: true,
    },
    {
      link: { name: "ReleaseNotes" },
      icon: "mdi-party-popper",
      title: i18n.t("releasenotes.title"),
      enabled: true,
    },
    // Every packet of every catalogue in one list. Packets are also listed in the table of the catalogue
    // they bundle from (`services/catalogPackets.js`) and have no drawer entry of their own; this is the
    // one page that shows them all together, and the palette is its way in.
    {
      link: { name: "PacketOverview" },
      icon: ENTITY_ICONS.packet,
      title: i18n.t("nav.packet.overview"),
      enabled: NAV_GATES["nav-packet"](),
    },
    // Categories are assigned and maintained where they are used — the category field of an
    // hourly-wage report creates, renames and deletes them in place — so the catalogue page has no
    // drawer entry any more. It stays reachable here for a look at the whole list.
    {
      link: { name: "CategoryOverview" },
      icon: ENTITY_ICONS.category,
      title: i18n.t("nav.category.overview"),
      enabled: NAV_GATES["nav-category"](),
    },
    {
      link: { name: "AuditOverview" },
      icon: "mdi-clipboard-text-clock-outline",
      title: i18n.t("audit.overview.title"),
      enabled: true,
    },
    // The feature's three pages for somebody else's time. They hang off "Meine Zeiten" today, which
    // is the right entry point when you are already looking at your own week and none at all when
    // you are not — exactly the case this list exists for. They reach each other through the
    // feature's own tab strip (`LewTimeTrackingNav`), so none of them needs a second sidebar entry —
    // but a page findable only by clicking through another page is one nobody finds twice.
    {
      link: { name: "TimeTrackingReview" },
      icon: "mdi-check-decagram-outline",
      title: i18n.t("timetracking.review.title"),
      parent,
      enabled: NAV_GATES["nav-timetracking-foreign"](),
    },
    // The Arbeitszeitkonto and the Verstöße are part of the page above — the account two columns of
    // every row, the findings a reading of the matrix — and they keep rows of their own here because
    // this is the only way to reach either **by name**. `#violations` opens the matrix in its
    // Verstöße reading; `#account` needs nothing opened, since the columns are always on screen, and
    // only says in the URL what the reader asked for.
    {
      link: { name: "TimeTrackingReview", hash: "#account" },
      icon: "mdi-scale-balance",
      title: i18n.t("timetracking.account.title"),
      parent,
      enabled: NAV_GATES["nav-timetracking-foreign"](),
    },
    {
      link: { name: "TimeTrackingReview", hash: "#violations" },
      icon: "mdi-gavel",
      title: i18n.t("timetracking.compliance.title"),
      parent,
      enabled: NAV_GATES["nav-timetracking-foreign"](),
    },
  ]
}

// ---------------------------------------------------------------------------- tabs
//
// Tabs are addressed by URL hash (`useTabHash`), so they are ordinary route targets and the command
// palette can jump straight into one. Only the palette uses these — the drawer links to whole views.
//
// The split below is about what a tab needs to resolve: a settings tab needs nothing, a project tab
// needs a project. So settings tabs are complete destinations, while project tabs are described
// without a project and `LewCommandPalette` pairs them with one.

/**
 * The Settings view's tabs, mirroring `SettingsOverview` — same hashes, same labels, same gates, so
 * the palette never offers a tab the view would hide.
 */
export function settingsTabItems(i18n) {
  const parent = i18n.t("nav.settings")
  return [
    { hash: "general", title: i18n.t("settings.general.title"), enabled: true },
    { hash: "terminology", title: i18n.t("settings.terminology.title"), enabled: true },
    { hash: "rights", title: i18n.t("settings.rights.title"), enabled: true },
    { hash: "absence", title: i18n.t("settings.absence.title"), enabled: true },
    { hash: "notification", title: i18n.t("notification.settings.title"), enabled: true },
    // The four below carry the gates `SettingsOverview` puts on them, and stand in its order:
    // without the permission (or, for Arbeitszeitmodelle, the add-on) the view does not render the
    // tab at all, so offering it here would land the user on the general tab.
    {
      // Two gates, exactly as `SettingsOverview` puts on the tab: the permission says whether this
      // user may read the models, the entitlement whether the workspace bought the add-on at all.
      hash: "worktimemodel",
      title: i18n.t("timetracking.model.title"),
      enabled: hasPermission(PERMISSIONS.VIEW_WORK_TIME_MODEL).value && isEntitled(FEATURE_SETS.TIME_TRACKING),
    },
    {
      // The add-on alone, as on `SettingsOverview`: workspace configuration needs no permission.
      hash: "pdftemplates",
      title: i18n.t("settings.pdf.title"),
      enabled: isEntitled(FEATURE_SETS.DOCUMENT_DESIGN),
    },
    {
      hash: "billing",
      title: i18n.t("billing.settings.title"),
      enabled: hasPermission(PERMISSIONS.VIEW_BILLING).value,
    },
    {
      // Was missing entirely, which left the data export reachable only by clicking through to
      // Settings and finding the tab — the one destination the palette could not offer.
      hash: "dataexport",
      title: i18n.t("dataexport.settings.title"),
      enabled: hasPermission(PERMISSIONS.EXPORT_TENANT_DATA).value,
    },
  ].map(tab => ({
    link: { name: "SettingsOverview", hash: `#${tab.hash}` },
    icon: "mdi-cog",
    title: tab.title,
    parent,
    enabled: tab.enabled,
  }))
}

/**
 * Every entity detail view's tabs, keyed by the entity type ids `services/globalSearch.js` and
 * `store/recentEntities.js` use, so a search hit, a recent entry and a tab catalogue all address a
 * type the same way.
 *
 * Each list mirrors its view (`LewProjectEdit`, `LewCustomerEdit`, …): same hashes — taken from the
 * views' own `*_FORM_NAMES` rather than retyped, since a drifted hash silently lands on the default
 * tab — same labels, and the same per-tab permission gates, so the palette never offers a tab the
 * view would hide.
 *
 * Returned without an entity. The caller pairs each with one and builds
 * `{ name: <detail route>, params: { <idParam>: id }, hash }`.
 */
export function entityTabItems(i18n) {
  // Project tabs are gated by an assigned/foreign permission *pair*; every other view gates on a
  // single permission (or a small any-of set).
  const projectTab = (value, titleKey, suffix) => ({
    hash: value,
    title: i18n.t(`project.detail.tabs.${titleKey}`),
    enabled: hasPermissionsOfAtLeastOne([
      PERMISSIONS[`VIEW_ASSIGNED_PROJECT_${suffix}`],
      PERMISSIONS[`VIEW_FOREIGN_PROJECT_${suffix}`],
    ]).value,
  })
  const tab = (value, titleKey, permissions) => ({
    hash: value,
    title: i18n.t(titleKey),
    enabled: hasPermissionsOfAtLeastOne(permissions).value,
  })

  return {
    PROJECT: [
      projectTab(PROJECT_FORM_NAMES.GENERAL, "general", "GENERAL"),
      projectTab(PROJECT_FORM_NAMES.CONTACTS, "contacts", "CONTACT"),
      projectTab(PROJECT_FORM_NAMES.PHASES, "phases", "PHASE"),
      projectTab(PROJECT_FORM_NAMES.SERVICE_CATALOG, "serviceCatalog", "SERVICE_CATALOG"),
      projectTab(PROJECT_FORM_NAMES.USERS, "users", "USER"),
      projectTab(PROJECT_FORM_NAMES.USER_POSITIONS, "userPositions", "USERPOSITION"),
      projectTab(PROJECT_FORM_NAMES.MACHINES, "machines", "MACHINE"),
      projectTab(PROJECT_FORM_NAMES.MATERIALS, "materials", "MATERIAL"),
      projectTab(PROJECT_FORM_NAMES.ADDITIONAL_EFFORTS, "additional_efforts", "ADDITIONAL_EFFORT"),
      projectTab(PROJECT_FORM_NAMES.HOURLY_WAGE_REPORTS, "hourlyWageReports", "HOURLYWAGEREPORT"),
      projectTab(PROJECT_FORM_NAMES.CONSTRUCTION_REPORTS, "constructionReports", "CONSTRUCTIONREPORT"),
      // Not a `projectTab`: this one is gated on the Zeiterfassung right and the add-on rather than
      // on an assigned/foreign project pair, exactly as the view gates it.
      {
        hash: PROJECT_FORM_NAMES.TIME_TRACKING,
        title: i18n.t("project.detail.tabs.timetracking"),
        enabled: NAV_GATES["nav-timetracking-foreign"](),
      },
      projectTab(PROJECT_FORM_NAMES.FILES, "files", "FILE"),
      projectTab(PROJECT_FORM_NAMES.CONFIGURATION, "configuration", "SETTINGS"),
      projectTab(PROJECT_FORM_NAMES.ACTIVITY, "activity", "GENERAL"),
    ],
    CUSTOMER: [
      tab(CUSTOMER_FORM_NAMES.GENERAL, "customer.detail.tabs.general", [PERMISSIONS.VIEW_CUSTOMER_GENERAL]),
      tab(CUSTOMER_FORM_NAMES.CONTACTS, "customer.detail.tabs.contacts", [PERMISSIONS.VIEW_CUSTOMER_CONTACT]),
      tab(CUSTOMER_FORM_NAMES.MACHINES, "customer.detail.tabs.machines", [PERMISSIONS.VIEW_CUSTOMER_MACHINE]),
      tab(CUSTOMER_FORM_NAMES.MATERIALS, "customer.detail.tabs.materials", [PERMISSIONS.VIEW_CUSTOMER_MATERIAL]),
      tab(CUSTOMER_FORM_NAMES.ADDITIONAL_EFFORTS, "customer.detail.tabs.additional_efforts", [
        PERMISSIONS.VIEW_CUSTOMER_ADDITIONAL_EFFORT,
      ]),
      tab(CUSTOMER_FORM_NAMES.FILES, "customer.detail.tabs.files", [PERMISSIONS.VIEW_CUSTOMER_FILE]),
      tab(CUSTOMER_FORM_NAMES.USER_POSITIONS, "customer.detail.tabs.userPositions", [
        PERMISSIONS.VIEW_CUSTOMER_USERPOSITION,
      ]),
      tab(CUSTOMER_FORM_NAMES.CONFIGURATION, "customer.detail.tabs.configuration", [
        PERMISSIONS.VIEW_CUSTOMER_SETTINGS,
      ]),
      tab(CUSTOMER_FORM_NAMES.ACTIVITY, "customer.detail.tabs.activity", [PERMISSIONS.VIEW_CUSTOMER_GENERAL]),
    ],
    USER: [
      tab(EMPLOYEE_FORM_NAMES.GENERAL, "employee.detail.tabs.general", [PERMISSIONS.VIEW_USER_GENERAL]),
      tab(EMPLOYEE_FORM_NAMES.COMPANY, "employee.detail.tabs.company", [
        PERMISSIONS.VIEW_USER_COMPANY_GENERAL,
        PERMISSIONS.VIEW_USER_COMPANY_SOCIAL_INSURANCE,
        PERMISSIONS.VIEW_USER_COMPANY_SOCIAL_INSURANCE_NUMBER,
        PERMISSIONS.VIEW_USER_COMPANY_PAY,
        PERMISSIONS.VIEW_USER_COMPANY_TOTAL_HOLIDAY_DAYS,
      ]),
      // The view additionally hides this tab for data users (no login), which is a property of the
      // employee rather than of the viewer — landing there falls back to the default tab.
      tab(EMPLOYEE_FORM_NAMES.SYSTEM, "employee.detail.tabs.system", [PERMISSIONS.VIEW_USER_SYSTEM]),
      tab(EMPLOYEE_FORM_NAMES.FILES, "employee.detail.tabs.files", [PERMISSIONS.VIEW_USER_FILE]),
      tab(EMPLOYEE_FORM_NAMES.EXTENDED, "employee.detail.tabs.extended", [PERMISSIONS.VIEW_USER_EXTENDED]),
      tab(EMPLOYEE_FORM_NAMES.ACTIVITY, "employee.detail.tabs.activity", [PERMISSIONS.VIEW_USER_GENERAL]),
    ],
    MATERIAL: [
      tab(MATERIAL_FORM_NAMES.GENERAL, "material.detail.tabs.general", [
        PERMISSIONS.VIEW_MATERIAL_GENERAL,
        PERMISSIONS.VIEW_MATERIAL_PURCHASEPRICE,
      ]),
      tab(MATERIAL_FORM_NAMES.FILES, "material.detail.tabs.files", [PERMISSIONS.VIEW_MATERIAL_GENERAL]),
      tab(MATERIAL_FORM_NAMES.ACTIVITY, "material.detail.tabs.activity", [PERMISSIONS.VIEW_MATERIAL_GENERAL]),
    ],
    MACHINE: [
      tab(MACHINE_FORM_NAMES.GENERAL, "machine.detail.tabs.general", [
        PERMISSIONS.VIEW_MACHINE_GENERAL,
        PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE,
      ]),
      tab(MACHINE_FORM_NAMES.FILES, "machine.detail.tabs.files", [PERMISSIONS.VIEW_MACHINE_GENERAL]),
      tab(MACHINE_FORM_NAMES.ACTIVITY, "machine.detail.tabs.activity", [PERMISSIONS.VIEW_MACHINE_GENERAL]),
    ],
    ADDITIONAL_EFFORT: [
      tab(ADDITIONAL_EFFORT_FORM_NAMES.GENERAL, "additionaleffort.detail.tabs.general", [
        PERMISSIONS.VIEW_ADDITIONAL_EFFORT_GENERAL,
        PERMISSIONS.VIEW_ADDITIONAL_EFFORT_PRICE,
      ]),
      tab(ADDITIONAL_EFFORT_FORM_NAMES.FILES, "additionaleffort.detail.tabs.files", [
        PERMISSIONS.VIEW_ADDITIONAL_EFFORT_GENERAL,
      ]),
      tab(ADDITIONAL_EFFORT_FORM_NAMES.ACTIVITY, "additionaleffort.detail.tabs.activity", [
        PERMISSIONS.VIEW_ADDITIONAL_EFFORT_GENERAL,
      ]),
    ],
    // A packet has no files of its own — it is a name and a list of ids — so it has the two tabs
    // every record has and no third.
    PACKET: [
      tab(PACKET_FORM_NAMES.GENERAL, "packet.detail.tabs.general", [PERMISSIONS.VIEW_PACKET_GENERAL]),
      tab(PACKET_FORM_NAMES.ACTIVITY, "packet.detail.tabs.activity", [PERMISSIONS.VIEW_PACKET_GENERAL]),
    ],
  }
}

/**
 * Create shortcuts offered by the command palette. A packet is created from its catalogue's packet
 * list with the kind already decided, so each kind is a shortcut of its own. The three titles
 * (`packet.create.*.title`) read the same, so each names its catalogue as `parent`, which the palette
 * shows and matches on exactly as it does for a destination, and carries the catalogue's icon as a
 * `badge` on the packet icon, so the three rows differ at a glance too.
 *
 * Every one of these targets a route guarded by `requiresAllPermissions`, hence `hasPermissionsOfAll`.
 */
export function quickActions(i18n) {
  return [
    {
      link: { name: "ProjectCreate" },
      icon: ENTITY_ICONS.project,
      title: i18n.t("project.create.title"),
      enabled: hasPermissionsOfAll([
        PERMISSIONS.EDIT_ASSIGNED_PROJECT_GENERAL,
        PERMISSIONS.EDIT_FOREIGN_PROJECT_GENERAL,
      ]).value,
    },
    {
      link: { name: "EmployeeCreate" },
      icon: ENTITY_ICONS.employee,
      title: i18n.t("employee.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_USER_GENERAL, PERMISSIONS.EDIT_USER_SYSTEM]).value,
    },
    {
      link: { name: "CustomerCreate" },
      icon: ENTITY_ICONS.customer,
      title: i18n.t("customer.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_CUSTOMER_GENERAL]).value,
    },
    {
      link: { name: "MaterialCreate" },
      icon: ENTITY_ICONS.material,
      title: i18n.t("material.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_MATERIAL_GENERAL]).value,
    },
    {
      link: { name: "MachineCreate" },
      icon: ENTITY_ICONS.machine,
      title: i18n.t("machine.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_MACHINE_GENERAL]).value,
    },
    {
      link: { name: "AdditionalEffortCreate" },
      icon: ENTITY_ICONS.additionaleffort,
      title: i18n.t("additionaleffort.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_ADDITIONAL_EFFORT_GENERAL]).value,
    },
    ...PACKET_TYPES.map(type => ({
      link: { name: CATALOG_PACKETS[type].create },
      icon: ENTITY_ICONS.packet,
      badge: CATALOG_PACKETS[type].icon,
      title: i18n.t(CATALOG_PACKETS[type].createTitleKey),
      parent: i18n.t(CATALOG_PACKETS[type].catalogNavKey),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_PACKET_GENERAL]).value,
    })),
    {
      link: { name: "CategoryCreate" },
      icon: ENTITY_ICONS.category,
      title: i18n.t("category.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_CATEGORY_GENERAL]).value,
    },
    {
      link: { name: "UserPositionCreate" },
      icon: ENTITY_ICONS.userposition,
      title: i18n.t("userposition.create.title"),
      enabled: hasPermissionsOfAll([PERMISSIONS.EDIT_USER_POSITION_GENERAL]).value,
    },
  ]
}
